import {
  Currency,
  CustomParameters,
  Duration,
  DurationUnit,
  PaymentMethod,
  PaymentMethodTypes,
  PaywallFeature,
  PaywallSession,
  StoredCreditCard,
  Subscription,
  Voucher,
} from '@accedo/one-plugin-sdk';
import { useLocation } from 'react-router';

import { axios } from '#/client/axios';
import { PackageDefinition, PurchaseMethod } from '#/client/models';
import { ClientPaywallApi } from '#/store/paywall/paywallApi';
import { tb } from '#/store/translation/translation.store';

import { CardStatus } from './models';

export const translatePrice = (
  price: string | number,
  currency: Currency | undefined,
  duration?: Duration,
  nextPayment?: Date
) => {
  // plugins should return "forever" duration unit for one-off products, but we
  // don't really handle that here, as we don't display prices for those anyway.

  // free product without next payment date
  if (String(price) === '0' && !nextPayment) {
    return tb('web.iap.price.free');
  }

  // this is a temporary workaround for Rural's use-case to avoid changing plugins.
  if (duration && duration.unit !== DurationUnit.Unknown) {
    const { count, unit } = duration;
    const pluralFormat = count > 1 ? '_plural' : '';

    return tb(`web.iap.price.periodical.${unit.toLowerCase()}${pluralFormat}`, {
      purchase: { price, currency: translateCurrency(currency) },
      count,
    });
  }

  return tb(`web.iap.price.oneOff`, {
    purchase: { price, currency: translateCurrency(currency) },
  });
};

export type PlanInformationById = Record<
  string,
  | {
      promotionalLabel?: string;
      description?: string;
      name?: string;
    }
  | undefined
>;

export const getPlanInformationById = (
  packageDefinition: PackageDefinition | undefined,
  getSafeId: (id: string | undefined) => string
): PlanInformationById | undefined => {
  if (!packageDefinition) {
    return undefined;
  }

  const { subscriptionGroups } = packageDefinition;

  const planEntries = subscriptionGroups.flatMap((group) =>
    group.tiers.flatMap((tier) =>
      tier.plans.map(({ web, promotionalLabel, description, name }) => [
        getSafeId(web?.productIdentifier),
        {
          promotionalLabel,
          description,
          name,
        },
      ])
    )
  );

  return Object.fromEntries(planEntries);
};

export const getPickerItemsFromProducts = (
  subscriptions: Subscription[],
  getSafeId: (id: string | undefined) => string,
  packageDefinition?: PackageDefinition
) => {
  const planInformationById = getPlanInformationById(
    packageDefinition,
    getSafeId
  );

  return subscriptions.map(
    ({
      id,
      title,
      description,
      price,
      currency,
      duration,
      nextPayment,
    }: Subscription) => {
      const planInformation = planInformationById?.[getSafeId(id)];

      return {
        id,
        title,
        subtitle: translateDescription(
          description,
          price,
          currency,
          nextPayment
        ),
        detail: translatePrice(price, currency, duration),
        promotionalLabel: planInformation?.promotionalLabel,
        description: planInformation?.description,
        name: planInformation?.name,
      };
    }
  );
};

export const translateDescription = (
  description: string,
  price: string,
  currency: Currency,
  date?: Date
) => {
  return tb(description, {
    date,
    purchase: {
      price,
      currency: translateCurrency(currency),
    },
    // duplicated for backward compatibility. remove later.
    price,
    currency: translateCurrency(currency),
  });
};

export const translateDiscount = (currency: Currency, voucher: Voucher) => {
  const {
    isPercent,
    discountAmount,
    duration: { count, unit },
  } = voucher;
  const discountKey = isPercent ? 'discountPercentage' : 'discountAbsolute';
  const unitKey = unit === DurationUnit.Forever ? 'forever' : `first${unit}`;

  return tb(`web.iap.voucher.${discountKey}.${unitKey}`, {
    count,
    purchase: {
      voucher: {
        [discountKey]: discountAmount,
      },
      currency: isPercent
        ? ''
        : translateCurrency(voucher.currency ?? currency),
    },
  });
};

export const translatePaymentMethodStatus = (
  status: CardStatus,
  card?: StoredCreditCard
) => {
  return tb(
    status === CardStatus.Valid
      ? `iap.paymentMethod.card.type.debit`
      : `iap.paymentMethod.card.status.${status}`,
    {
      purchase: {
        ...(card && {
          card: {
            type: card.cardType,
            endingNumbers: card.cardNumber.slice(card.cardNumber.length - 4),
          },
        }),
      },
    }
  );
};

export const getPaywallSession = (sessionEndpoint: string) => {
  return axios.request<PaywallSession>({
    url: sessionEndpoint,
    method: 'GET',
  });
};

export const translateCurrency = (currency = Currency.UNKNOWN) => {
  return tb(`web.iap.currency.${currency.toLowerCase()}`, {
    defaultValue: currency,
  });
};

/**
 * Take the `vendorTag` out of the URL, and return it as a `CustomParameters` object.
 * We don't verify the allowed values anymore.
 * @returns {CustomParameters|undefined} Object with `vendorTag` in it, or undefined.
 */
export const useSubscriptionParameters = (): CustomParameters | undefined => {
  const location = useLocation();

  const vendorTag = new URLSearchParams(location.search).get('vendorTag') ?? '';

  if (vendorTag) {
    return { vendorTag };
  }

  return undefined;
};

/**
 * Checks a list of features to see if they are supported.
 *
 * @returns A list of boolean values matching the support for each given feature
 */
export const supportsFeatures = (
  featuresToCheck: PaywallFeature[],
  supportedFeatures: PaywallFeature[]
): boolean[] =>
  featuresToCheck.map((feature) => supportedFeatures?.includes(feature));

export const coreToSdkType: Partial<
  Record<PurchaseMethod, PaymentMethodTypes>
> = {
  CREDIT_CARD: PaymentMethodTypes.CreditCard,
  PAYPAL: PaymentMethodTypes.Paypal,
};

export const convertSdkTypeToCore = (
  paymentMethod: PaymentMethod | undefined
) => {
  const [key] =
    Object.entries(coreToSdkType).find(
      ([_key, value]) => value === paymentMethod?.type
    ) || [];

  return (key as PurchaseMethod) ?? 'UNKNOWN';
};

const getOtherPlatformMessageKey = (
  purchaseMethodTranslationKey: string | undefined
) => {
  if (
    !purchaseMethodTranslationKey ||
    purchaseMethodTranslationKey === 'unknownSubscription'
  ) {
    return 'iap.unknownSubscription.message';
  }

  return `iap.knownSubscription.${purchaseMethodTranslationKey}.message`;
};

/** Was the subscription returned by Core purchased on a different platform or with an unsupported payment method? */
export const getOtherPlatformInfo = (
  paywallApi: ClientPaywallApi,
  isPaywallReady: boolean,
  purchaseMethod: PurchaseMethod | undefined,
  purchaseMethodTranslationKey: string | undefined
) => {
  if (!purchaseMethod || !isPaywallReady) {
    return { isOtherPlatform: false };
  }

  const usedType = coreToSdkType[purchaseMethod] ?? PaymentMethodTypes.Unknown;
  const supportedTypes = paywallApi.supportedPaymentMethodTypes?.() ?? [];
  const isOtherPlatform = !supportedTypes.includes(usedType);
  const otherPlatformMessageKey = getOtherPlatformMessageKey(
    purchaseMethodTranslationKey
  );

  return { isOtherPlatform, otherPlatformMessageKey };
};
